import React, { useState, Component } from 'react';
import { ArrowRightIcon, DropletIcon, UsersIcon, ShareIcon, CheckIcon, SendIcon, SmartphoneIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
import Button from '../ui/Button';
import GlassCard from '../ui/GlassCard';
import { useHydration } from '../../contexts/HydrationContext';
interface OnboardingProps {
  onComplete: () => void;
}
const Onboarding: React.FC<OnboardingProps> = ({
  onComplete
}) => {
  const {
    updateProfile
  } = useHydration();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    weight: '',
    activityLevel: 'moderate',
    location: '',
    earnRewards: true,
    announceJoined: true
  });
  const [invitedFriends, setInvitedFriends] = useState<string[]>([]);
  const [newFriend, setNewFriend] = useState('');
  const [showSharePreview, setShowSharePreview] = useState(false);
  const [shareLocation, setShareLocation] = useState('Smart Soda Fountain');
  const locations = ['Eco Café', 'Fitness Center', 'Hydration Lounge at One Health Clubs', 'Green Hotel', 'Downtown Smart Soda Hub', 'University Campus', 'Central Park Kiosk', 'Organic Market'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleAddFriend = () => {
    if (newFriend.trim() && !invitedFriends.includes(newFriend.trim())) {
      setInvitedFriends([...invitedFriends, newFriend.trim()]);
      setNewFriend('');
    }
  };
  const handleShareViaWhatsApp = () => {
    setShowSharePreview(true);
    setShareLocation('Smart Soda Fountain');
  };
  const handleShareViaSMS = () => {
    setShowSharePreview(true);
    setShareLocation('Smart Soda Fountain');
  };
  const handleSendInvite = () => {
    const message = `Join me at ${shareLocation} for a Smart Soda! Download the app now and get your first drink free!`;
    // In a real app, this would use the Web Share API or open the respective app
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    setShowSharePreview(false);
  };
  const nextStep = () => {
    if (step === 4) {
      updateProfile({
        name: formData.name,
        age: parseInt(formData.age),
        weight: parseInt(formData.weight),
        activityLevel: formData.activityLevel,
        location: formData.location,
        earnRewards: formData.earnRewards,
        announceJoined: formData.announceJoined
      });
      onComplete();
    } else {
      setStep(prev => prev + 1);
    }
  };
  const renderStep = () => {
    switch (step) {
      case 0:
        return <div className="flex flex-col items-center text-center" data-id="element-962">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bjQZYUmVLVPHQytWYM7VAn/SS-Logo.png" alt="Smart Soda Logo" className="w-48 h-48 object-contain mb-6" data-id="element-963" />
            <h1 className="text-3xl font-bold text-[#FAFAFA] mb-4 font-gt-ultra" data-id="element-964">
              Smart Soda Hydrate+
            </h1>
            <p className="text-[#00E4BB] text-lg mb-4 font-gill-sans" data-id="element-965">
              Revolutionizing hydration for a sustainable tomorrow
            </p>
            <p className="text-[#607D8B] mb-8 text-sm max-w-xs" data-id="element-966">
              Track, earn, and make a difference with every smart sip
            </p>
            <div className="w-full max-w-xs" data-id="element-967">
              <Button fullWidth onClick={nextStep} data-id="element-968">
                Get Started <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-969" />
              </Button>
            </div>
          </div>;
      case 1:
        return <div className="w-full max-w-xs" data-id="element-970">
            <h2 className="text-2xl font-bold text-white mb-6 font-gt-ultra" data-id="element-971">
              Tell us about yourself
            </h2>
            <div className="space-y-4" data-id="element-972">
              <div data-id="element-973">
                <label className="block text-[#00E4BB] mb-2 text-sm font-gill-sans" data-id="element-974">
                  Your Name
                </label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" placeholder="Enter your name" data-id="element-975" />
              </div>
              <div data-id="element-976">
                <label className="block text-[#00E4BB] mb-2 text-sm font-gill-sans" data-id="element-977">
                  Age
                </label>
                <input type="number" name="age" value={formData.age} onChange={handleInputChange} className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" placeholder="Enter your age" data-id="element-978" />
              </div>
              <div data-id="element-979">
                <label className="block text-[#00E4BB] mb-2 text-sm font-gill-sans" data-id="element-980">
                  Preferred Location
                </label>
                <select name="location" value={formData.location} onChange={handleInputChange} className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-981">
                  <option value="" data-id="element-982">Select your preferred location</option>
                  {locations.map((location, index) => <option key={index} value={location} data-id="element-983">
                      {location}
                    </option>)}
                </select>
              </div>
              <div className="flex items-center mt-2" data-id="element-984">
                <input type="checkbox" id="announceJoined" name="announceJoined" checked={formData.announceJoined} onChange={handleCheckboxChange} className="w-4 h-4 text-[#00E4BB] bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded focus:ring-[#00E4BB]" data-id="element-985" />
                <label htmlFor="announceJoined" className="ml-2 text-sm text-white" data-id="element-986">
                  Announce my presence to others at this location only
                </label>
              </div>
              <div className="flex items-center mt-2" data-id="element-987">
                <input type="checkbox" id="earnRewards" name="earnRewards" checked={formData.earnRewards} onChange={handleCheckboxChange} className="w-4 h-4 text-[#00E4BB] bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded focus:ring-[#00E4BB]" data-id="element-988" />
                <label htmlFor="earnRewards" className="ml-2 text-sm text-white" data-id="element-989">
                  I'd like to earn free drinks and prizes
                </label>
              </div>
              <Button fullWidth onClick={nextStep} className="mt-6" data-id="element-990">
                Continue <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-991" />
              </Button>
            </div>
          </div>;
      case 2:
        return <div className="w-full max-w-xs" data-id="element-992">
            <h2 className="text-2xl font-bold text-white mb-6 font-gt-ultra" data-id="element-993">
              Your Hydration Profile
            </h2>
            <div className="space-y-4" data-id="element-994">
              <div data-id="element-995">
                <label className="block text-[#00E4BB] mb-2 text-sm font-gill-sans" data-id="element-996">
                  Weight (kg)
                </label>
                <input type="number" name="weight" value={formData.weight} onChange={handleInputChange} className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" placeholder="Enter your weight" data-id="element-997" />
              </div>
              <div data-id="element-998">
                <label className="block text-[#00E4BB] mb-2 text-sm font-gill-sans" data-id="element-999">
                  Activity Level
                </label>
                <select name="activityLevel" value={formData.activityLevel} onChange={handleInputChange} className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-1000">
                  <option value="low" data-id="element-1001">Low - Mostly sedentary</option>
                  <option value="moderate" data-id="element-1002">Moderate - Regular activity</option>
                  <option value="high" data-id="element-1003">High - Very active daily</option>
                </select>
              </div>
              <Button fullWidth onClick={nextStep} className="mt-6" data-id="element-1004">
                Continue <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-1005" />
              </Button>
            </div>
          </div>;
      case 3:
        return <div className="w-full max-w-xs" data-id="element-1006">
            <h2 className="text-2xl font-bold text-white mb-6 font-gt-ultra" data-id="element-1007">
              Enable Features
            </h2>
            <div className="space-y-4" data-id="element-1008">
              <GlassCard className="flex items-center p-4" data-id="element-1009">
                <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1010">
                  <DropletIcon size={20} className="text-[#00E4BB]" data-id="element-1011" />
                </div>
                <div className="flex-1" data-id="element-1012">
                  <h3 className="text-white font-medium" data-id="element-1013">Location Services</h3>
                  <p className="text-sm text-gray-300" data-id="element-1014">
                    Find Smart Soda fountains near you
                  </p>
                </div>
                <div className="ml-2" data-id="element-1015">
                  <div className="w-12 h-6 bg-[#00E4BB]/30 rounded-full p-1 cursor-pointer" data-id="element-1016">
                    <div className="w-4 h-4 bg-[#00E4BB] rounded-full transform translate-x-6" data-id="element-1017"></div>
                  </div>
                </div>
              </GlassCard>
              <GlassCard className="flex items-center p-4" data-id="element-1018">
                <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1019">
                  <DropletIcon size={20} className="text-[#00E4BB]" data-id="element-1020" />
                </div>
                <div className="flex-1" data-id="element-1021">
                  <h3 className="text-white font-medium" data-id="element-1022">Health Connect</h3>
                  <p className="text-sm text-gray-300" data-id="element-1023">
                    Sync with Apple Health / Google Fit
                  </p>
                </div>
                <div className="ml-2" data-id="element-1024">
                  <div className="w-12 h-6 bg-[#00E4BB]/30 rounded-full p-1 cursor-pointer" data-id="element-1025">
                    <div className="w-4 h-4 bg-[#00E4BB] rounded-full transform translate-x-6" data-id="element-1026"></div>
                  </div>
                </div>
              </GlassCard>
              <Button fullWidth onClick={nextStep} className="mt-6" data-id="element-1027">
                Continue <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-1028" />
              </Button>
            </div>
          </div>;
      case 4:
        return <div className="w-full max-w-xs" data-id="element-1029">
            <h2 className="text-3xl font-bold text-white mb-6 font-gt-ultra" data-id="element-1030">
              Invite Your Friends
            </h2>
            <div className="mb-8" data-id="element-1031">
              <GlassCard className="p-5 mb-6 border-l-4 border-l-[#A6FF4D]" data-id="element-1032">
                <div className="flex items-start" data-id="element-1033">
                  <div className="w-12 h-12 rounded-full bg-[#A6FF4D]/20 flex items-center justify-center mr-4 flex-shrink-0 mt-1" data-id="element-1034">
                    <UsersIcon size={24} className="text-[#A6FF4D]" data-id="element-1035" />
                  </div>
                  <div data-id="element-1036">
                    <h3 className="text-white font-medium text-xl mb-2" data-id="element-1037">
                      Better Together!
                    </h3>
                    <p className="text-gray-300 mb-3" data-id="element-1038">
                      People are{' '}
                      <span className="text-[#A6FF4D] font-bold" data-id="element-1039">
                        78% more likely
                      </span>{' '}
                      to hit their hydration goals when they do it with friends.
                    </p>
                    <p className="text-[#00E4BB] font-bold text-lg" data-id="element-1040">
                      Their first drink is on us! 🎉
                    </p>
                  </div>
                </div>
              </GlassCard>
              {/* Quick Share Options */}
              <div className="mb-7 space-y-4" data-id="element-1041">
                <h3 className="text-[#00E4BB] text-base font-medium" data-id="element-1042">
                  Quick Share:
                </h3>
                <Button fullWidth className="bg-[#25D366] border-none text-white justify-start py-4" onClick={handleShareViaWhatsApp} data-id="element-1043">
                  <div className="flex items-center" data-id="element-1044">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4" data-id="element-1045">
                      <ShareIcon size={18} className="text-white" data-id="element-1046" />
                    </div>
                    <span className="text-lg" data-id="element-1047">Share via WhatsApp</span>
                  </div>
                </Button>
                <Button fullWidth className="bg-[#3498db] border-none text-white justify-start py-4" onClick={handleShareViaSMS} data-id="element-1048">
                  <div className="flex items-center" data-id="element-1049">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4" data-id="element-1050">
                      <SmartphoneIcon size={18} className="text-white" data-id="element-1051" />
                    </div>
                    <span className="text-lg" data-id="element-1052">Share via SMS</span>
                  </div>
                </Button>
              </div>
              <div className="relative mb-6" data-id="element-1053">
                <div className="absolute inset-0 flex items-center" data-id="element-1054">
                  <div className="w-full border-t border-[#00E4BB]/20" data-id="element-1055"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-1056">
                  <span className="bg-[#1A237E] px-4 py-1 text-sm text-[#00E4BB]" data-id="element-1057">
                    OR ADD MANUALLY
                  </span>
                </div>
              </div>
              <div className="flex mb-5" data-id="element-1058">
                <input type="text" value={newFriend} onChange={e => setNewFriend(e.target.value)} placeholder="Friend's contact" className="flex-1 bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-l-lg px-4 py-4 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB] text-base" data-id="element-1059" />
                <button onClick={handleAddFriend} className="bg-[#00E4BB] text-[#1A237E] px-5 rounded-r-lg font-medium text-base" data-id="element-1060">
                  Add
                </button>
              </div>
              {invitedFriends.length > 0 && <div className="mb-5" data-id="element-1061">
                  <p className="text-[#00E4BB] mb-3 text-base font-gill-sans" data-id="element-1062">
                    Friends to invite:
                  </p>
                  <div className="space-y-3 max-h-32 overflow-y-auto" data-id="element-1063">
                    {invitedFriends.map((friend, index) => <div key={index} className="flex items-center bg-[#1A237E]/80 rounded-lg px-4 py-3" data-id="element-1064">
                        <CheckIcon size={18} className="text-[#A6FF4D] mr-3" data-id="element-1065" />
                        <span className="text-white" data-id="element-1066">{friend}</span>
                      </div>)}
                  </div>
                </div>}
              {invitedFriends.length > 0 && <Button fullWidth variant="primary" className="bg-[#A6FF4D] text-[#1A237E] border-none mb-5 py-4" onClick={() => {}} data-id="element-1067">
                  <SendIcon className="mr-3 w-5 h-5" data-id="element-1068" /> Send{' '}
                  {invitedFriends.length} Invites
                </Button>}
            </div>
            <Button variant="outline" fullWidth onClick={nextStep} className="mt-2 py-3" data-id="element-1069">
              Continue <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1070" />
            </Button>
          </div>;
      default:
        return null;
    }
  };
  return <div className="h-full bg-[#1A237E] flex flex-col items-center justify-center p-6" data-id="element-1071">
      {/* Progress dots */}
      {step > 0 && <div className="flex space-x-2 mb-8" data-id="element-1072">
          {[1, 2, 3, 4].map(dotStep => <div key={dotStep} className={`w-2 h-2 rounded-full ${step >= dotStep ? 'bg-[#00E4BB]' : 'bg-[#607D8B]'}`} data-id="element-1073"></div>)}
        </div>}
      {renderStep()}
      {/* Share Preview Modal */}
      {showSharePreview && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-1074">
          <div className="bg-white rounded-xl p-5 w-full max-w-xs" data-id="element-1075">
            <h3 className="text-lg font-bold text-[#1A237E] mb-4" data-id="element-1076">
              Invite Preview
            </h3>
            <div className="bg-gray-100 p-4 rounded-lg mb-4" data-id="element-1077">
              <div className="flex items-center mb-3" data-id="element-1078">
                <div className="w-10 h-10 rounded-full bg-[#1A237E] flex items-center justify-center mr-3" data-id="element-1079">
                  <DropletIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1080" />
                </div>
                <div data-id="element-1081">
                  <p className="font-medium text-[#1A237E]" data-id="element-1082">
                    Smart Soda Invite
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1083">From: {formData.name}</p>
                </div>
              </div>
              <p className="text-[#1A237E] mb-3" data-id="element-1084">
                Join me at {shareLocation} for a Smart Soda! Download the app
                now and get your first drink free!
              </p>
              <div className="flex items-center text-xs text-gray-500" data-id="element-1085">
                <MapPinIcon className="w-3 h-3 mr-1" data-id="element-1086" />
                <select value={shareLocation} onChange={e => setShareLocation(e.target.value)} className="bg-transparent text-xs border-b border-gray-300 focus:outline-none text-[#1A237E]" data-id="element-1087">
                  {locations.map((location, index) => <option key={index} value={location} data-id="element-1088">
                      {location}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="flex space-x-3" data-id="element-1089">
              <Button variant="outline" className="flex-1 border-[#1A237E] text-[#1A237E]" onClick={() => setShowSharePreview(false)} data-id="element-1090">
                Cancel
              </Button>
              <Button className="flex-1 bg-[#25D366] border-none text-white" onClick={handleSendInvite} data-id="element-1091">
                Send Invite
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};
export default Onboarding;